export interface CookieOptions {
  expires?: number // days
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

/**
 * Check if cookies are available
 */
export const isCookieAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined') return false

    const testKey = '__cookie_test__'
    setCookie(testKey, 'test', { expires: 1 })
    const result = getCookie(testKey) === 'test'
    deleteCookie(testKey)
    return result
  } catch {
    return false
  }
}

/**
 * Set a cookie
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  try {
    if (typeof window === 'undefined') return

    const {
      expires = 30,
      path = '/',
      domain,
      secure = window.location.protocol === 'https:',
      sameSite = 'Lax',
    } = options

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(
      value,
    )}`

    if (expires > 0) {
      const date = new Date()
      date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000)
      cookieString += `; expires=${date.toUTCString()}`
    }

    cookieString += `; path=${path}`

    if (domain) {
      cookieString += `; domain=${domain}`
    }

    if (secure) {
      cookieString += `; secure`
    }

    cookieString += `; samesite=${sameSite}`

    document.cookie = cookieString
  } catch (error) {
    console.error('Cookie set error:', error)
  }
}

/**
 * Get a cookie value
 */
export const getCookie = (name: string): string | null => {
  try {
    if (typeof window === 'undefined') return null

    const nameEQ = `${encodeURIComponent(name)}=`
    const cookies = document.cookie.split(';')

    for (let cookie of cookies) {
      let c = cookie.trim()
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length))
      }
    }

    return null
  } catch (error) {
    console.error('Cookie get error:', error)
    return null
  }
}

/**
 * Delete a cookie
 */
export const deleteCookie = (
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain'> = {},
): void => {
  const { path = '/', domain } = options
  setCookie(name, '', { ...options, expires: -1, path, domain })
}

/**
 * Get multiple cookies by prefix
 */
export const getCookiesByPrefix = (prefix: string): Record<string, string> => {
  const cookies: Record<string, string> = {}

  try {
    if (typeof window === 'undefined') return cookies

    document.cookie.split(';').forEach(cookie => {
      const [name, value] = cookie.trim().split('=')
      const decodedName = decodeURIComponent(name)

      if (decodedName.startsWith(prefix)) {
        const key = decodedName.replace(prefix, '')
        cookies[key] = decodeURIComponent(value || '')
      }
    })
  } catch (error) {
    console.error('Get cookies by prefix error:', error)
  }

  return cookies
}

/**
 * Delete cookies by prefix
 */
export const deleteCookiesByPrefix = (
  prefix: string,
  options?: Pick<CookieOptions, 'path' | 'domain'>,
): void => {
  try {
    const cookies = getCookiesByPrefix(prefix)
    Object.keys(cookies).forEach(key => {
      deleteCookie(`${prefix}${key}`, options)
    })
  } catch (error) {
    console.error('Delete cookies by prefix error:', error)
  }
}
